import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ListPageTopBar } from "@/components/layout/dashboard-layout";
import { useTable } from "@/hooks/useTable";
import { ListFooter } from "@/components/shared/list-footer";

import { ErrorBoundary } from "react-error-boundary";
import { RecordNotFound } from "@/components/shared/record-not-found";
import { TableColumnsHandle } from "@/components/shared/table-columns-handle";
import { ArrowLeftIcon, Columns3Icon, FilterIcon, X } from "lucide-react";
import { useMutations } from "./utils/react-hook/use-mutations";
import { ReactForm, ReactTable, ReactTableSkelton } from "./utils";
import { Fragment } from "react/jsx-runtime";
import ErrorFallback from "@/components/shared/error-fallback";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { CustomErrorPopup } from "@/components/shared/custom-error-popup";
import { useUserInfo } from "@/global-data/use-user-info";

export const TerminalKey = () => {
  const {
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    skeltonLoading,
    tableColumn,
    setTableColumn,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    retailerList,
    errorMessage,
    closeErrorMessage,
  } = useMutations();

  const {
    tableHeader,
    table,
    tableKeys,
    // toggleSelection,
    // rowSelection,
    onResize,
    onSorting,
    // loading,
  } = useTable({
    data: listData,
    view: ["stop_key", "retailer_id", "modified_by"],
    verifyKey: "terminal-key",
    visible: tableColumn,
    avoid: [],
  });

  const { activePermission } = useUserInfo();

  return (
    <Fragment>
      {formPopupOpen ? (
        <div className="flex flex-col gap-5">
          <ListPageTopBar
            title={
              activeRecordData ? "Update Terminal Key" : "Create Terminal Key"
            }
          >
            <Button
              variant={"action"}
              onClick={() => {
                onFormOpenChange(false);
                closeErrorMessage();
              }}
            >
              <ArrowLeftIcon /> Back
            </Button>
          </ListPageTopBar>
          <Card className="pt-6">
            <ReactForm
              formData={activeRecordData}
              onFormSubmit={onFormSubmit}
              onOpenChange={onFormOpenChange}
              open={formPopupOpen}
              retailerList={retailerList}
            />
          </Card>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <ListPageTopBar title="Terminal Key">
            <TableColumnsHandle
              tableKeys={tableKeys}
              tableColumn={tableColumn}
              setTableColumn={setTableColumn}
              onSorting={onSorting}
              labels={{
                stop_key: "Stop Key",
                retailer_id: "Retailer Name",
                modified_by: "Modified By",
              }}
            >
              <Columns3Icon /> Column
            </TableColumnsHandle>

            <ConditionRenderer condition={Object.keys(filter).length !== 0}>
              <>
                <Button variant={"action"} onClick={filterRequest}>
                  <FilterIcon /> Filter
                </Button>
                <Button variant={"action"} onClick={clearFilter}>
                  <X /> Clear Filter
                </Button>
              </>
            </ConditionRenderer>
            {activePermission?.create ? (
              <Button variant={"action"} onClick={() => onFormOpenChange(true)}>
                + Create
              </Button>
            ) : null}
          </ListPageTopBar>

          {skeltonLoading ? (
            <ReactTableSkelton />
          ) : (
            <Card className="overflow-hidden">
              <CardContent className="max-w-[100%] p-0 overflow-auto scrollbar h-auto min-h-[350px] md:h-[calc(100dvh-340px)]">
                <ErrorBoundary
                  fallbackRender={({ error, resetErrorBoundary }) => (
                    <ErrorFallback
                      error={error}
                      errorInfo={error.stack || ""}
                      resetErrorBoundary={resetErrorBoundary}
                    />
                  )}
                >
                  <ReactTable
                    tableHeader={tableHeader}
                    onResize={onResize}
                    table={table}
                    permission={permission}
                    onAction={onAction}
                    listData={listData}
                    filter={filter}
                    setFilter={setFilter}
                    filterRequest={filterRequest}
                    retailerList={retailerList}
                    activePermission={activePermission}
                  />
                  {table.length === 0 ? <RecordNotFound /> : null}
                </ErrorBoundary>
              </CardContent>
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                  <ErrorFallback
                    error={error}
                    errorInfo={error.stack || ""}
                    resetErrorBoundary={resetErrorBoundary}
                  />
                )}
              >
                <hr />
                <ListFooter
                  onPageChange={onPageChange}
                  onPerPageChange={onPerPageChange}
                  page={paginationData.page}
                  perPage={paginationData.perPage}
                  totalItem={paginationData.totalItem}
                  className="px-5 py-5"
                  isEmpty={table.length === 0}
                />
              </ErrorBoundary>
            </Card>
          )}
        </div>
      )}
      <CustomErrorPopup
        title={errorMessage?.title}
        description={errorMessage?.message}
        open={errorMessage?.open}
        setOpen={closeErrorMessage}
      />
    </Fragment>
  );
};
